import { Suspense } from 'react';
import { Outlet, RouteObject, ScrollRestoration, createBrowserRouter } from 'react-router-dom';

import NotFoundPage from './common/NotFoundPage';
import RouteErrorBoundary from './common/RouteErrorBoundary';
import Loading from 'src/components/common/Loading';

async function pickRoutes(): Promise<RouteObject[]> {
  const host = window.location.hostname;

  if (host.endsWith('auth.hyunsub.kim')) {
    return (await import('./auth/AuthIndex')).AuthRouteObjects;
  }

  if (host.endsWith('video.hyunsub.kim')) {
    return (await import('./video/VideoIndex')).VideoRouteObjects;
  }

  if (host.endsWith('photo.hyunsub.kim')) {
    return (await import('./photo/PhotoIndex')).PhotoRouteObjects;
  }

  if (host.endsWith('apparel.hyunsub.kim')) {
    return (await import('./apparel/ApparelIndex')).ApparelRouteObjects;
  }

  if (host.endsWith('drive.hyunsub.kim')) {
    return (await import('./drive/DriveIndex')).DriveRouteObjects;
  }

  if (host.endsWith('comic.hyunsub.kim')) {
    return (await import('./comic/ComicIndex')).ComicRouteObjects;
  }

  if (host.endsWith('apps.hyunsub.kim')) {
    return (await import('./apps/AppsIndex')).AppsRouteObjects;
  }

  if (host.endsWith('diary.hyunsub.kim')) {
    return (await import('./diary/DiaryIndex')).DiaryRouteObjects;
  }

  if (host.endsWith('friend.hyunsub.kim')) {
    return (await import('./friend/FriendIndex')).FriendRouteObjects;
  }

  if (host.endsWith('dutch.hyunsub.kim')) {
    return (await import('./dutch/DutchIndex')).DutchRouteObjects;
  }

  if (host.endsWith('encode.hyunsub.kim')) {
    return (await import('./encode/EncodeIndex')).EncodeRouteObjects;
  }

  if (host.endsWith('archive.hyunsub.kim')) {
    return (await import('./archive/ArchiveIndex')).ArchiveRouteObjects;
  }

  if (host.endsWith('timeline.hyunsub.kim')) {
    return (await import('./timeline/TimelineIndex')).TimelineRouteObjects;
  }

  if (host.endsWith('chat.hyunsub.kim')) {
    return (await import('./chat/ChatIndex')).ChatRouteObjects;
  }

  return [];
}

type AppRouter = ReturnType<typeof createBrowserRouter>;

/**
 * host별 서비스 라우트 모듈을 동적 import로만 불러오기 위해 router는 비동기로 초기화된다.
 * initRouter() 완료 전까지는 아직 설정되지 않은 상태이며, App이 초기화가 끝난 뒤에만 RouterProvider를 렌더링하므로
 * router.navigate(...)를 호출하는 페이지 훅들은 항상 초기화 이후에만 실행된다.
 */
let router: AppRouter = null as unknown as AppRouter;

export async function initRouter(): Promise<AppRouter> {
  const routeObjects = await pickRoutes();

  router = createBrowserRouter([
    {
      errorElement: <RouteErrorBoundary />,
      element: (
        <>
          <Suspense fallback={<Loading height="full" />}>
            <Outlet />
          </Suspense>
          <ScrollRestoration />
        </>
      ),
      children: [{ path: '*', element: <NotFoundPage /> }, ...routeObjects],
    },
  ]);

  return router;
}

export { router as default };
